export default function PricingSection() {
  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-3 bg-teal-50 rounded-full mb-6">
            <i className="ri-money-dollar-circle-line text-teal-600 text-xl mr-3"></i>
            <span className="text-teal-600 font-bold text-sm">Pricing</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            M&Aにおける譲渡価格について
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            買収する側も、譲渡する側も一番大切になってくるのが譲渡価格になります
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* 買収サイドの視点 */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-shopping-cart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900">買収サイドの視点</h3>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                買収サイドからしますと、大事なのは、<strong className="text-teal-600">何年でペイするか（元が取れるか）</strong>という点になります。
              </p>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6">
                <p className="text-sm text-gray-700 mb-2">適正な回収期間</p>
                <p className="text-3xl font-black text-gray-900">2〜5年</p>
                <p className="text-xs text-gray-600 mt-2">※業種などにもよりますが、中小零細企業のM&Aですと短い期間での回収が望まれることが多くなってきます</p>
              </div>
              <p className="text-gray-700 leading-relaxed">
                中小零細企業の場合、決算書上の利益よりも、<strong className="text-teal-600">現金の収入やキャッシュフロー</strong>のほうが重視されます。
              </p>
            </div>
          </div>

          {/* のれん代について */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900">のれん代について</h3>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                企業の価値として、数字に反映されない、所謂<strong className="text-teal-600">のれん代</strong>というものが発生してきます。
              </p>
              <div className="space-y-3">
                {[
                  { icon: 'ri-lightbulb-line', text: '事業のノウハウ' },
                  { icon: 'ri-group-line', text: '顧客・スタッフの引継ぎ' },
                  { icon: 'ri-time-line', text: '事業立ち上げの手間' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3">
                      <i className={`${item.icon} text-xl text-white`}></i>
                    </div>
                    <span className="font-bold text-gray-900">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                特に小規模なM&Aの場合では、のれん代の割合が譲渡金額に占める割合が大きくなってきます。
              </p>
            </div>
          </div>
        </div>

        {/* 価格設定のアドバイス */}
        <div className="bg-gradient-to-br from-gray-900 to-slate-800 rounded-3xl shadow-2xl p-12 text-white">
          <div className="flex items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mr-4">
              <i className="ri-chat-check-line text-3xl text-white"></i>
            </div>
            <h3 className="text-3xl font-black">価格設定のアドバイス</h3>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h4 className="font-black text-xl mb-3 flex items-center">
                <i className="ri-eye-line text-teal-400 mr-3"></i>
                買主様の目線を考慮
              </h4>
              <p className="text-gray-300 leading-relaxed">
                売主様としては、上記のような買主様の目線も考えたうえでの譲渡・買収金額の提示となります。
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h4 className="font-black text-xl mb-3 flex items-center">
                <i className="ri-exchange-line text-teal-400 mr-3"></i>
                一般的な流れ
              </h4>
              <p className="text-gray-300 leading-relaxed">
                一番多いやり方は、<strong className="text-teal-400">ある程度の値段で出して、先方のデューデリジェンスを受けての再度交渉</strong>というパターンです。
              </p>
            </div>

            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-l-4 border-amber-500 rounded-2xl p-6">
              <h4 className="font-black text-xl mb-3 flex items-center">
                <i className="ri-alert-line text-amber-400 mr-3"></i>
                注意点
              </h4>
              <p className="text-gray-300 leading-relaxed">
                ただしあまり高い金額を提示すると、交渉までも至らないということも実際に多くございます。
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h4 className="font-black text-xl mb-3 flex items-center">
                <i className="ri-customer-service-line text-teal-400 mr-3"></i>
                弊社のサポート
              </h4>
              <p className="text-gray-300 leading-relaxed">
                ある程度のアドヴァイスは弊社としても差し上げますので、そのうえで<strong className="text-teal-400">これなら売っても良いという金額</strong>を決めていただくことになります。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
